import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Check, Copy, ListChecks, Mail, Plus, Send, Sparkles, StopCircle, Timer, Trash2, Zap } from "lucide-react";
import { ModalShell, Field } from "../_dialog";
import { SignalGrid } from "./SignalGrid";
import { STATUS_LABEL, fmtDate } from "./constants";

export function ProspectDetailDialog({ prospectId, catalog, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [convertBusy, setConvertBusy] = useState(false);
  const [nurtureBusy, setNurtureBusy] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", channel: "email", due_date: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [p, t, s] = await Promise.all([
        api.get(`/opportunity/prospects/${prospectId}`),
        api.get(`/opportunity/tasks?prospect_id=${prospectId}`),
        api.get(`/opportunity/prospects/${prospectId}/nurture`),
      ]);
      setData(p.data);
      setTasks(t.data?.tasks || []);
      setSequences(s.data?.sequences || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId]);

  const patch = async (update) => {
    setSaving(true);
    try {
      const r = await api.patch(`/opportunity/prospects/${prospectId}`, update);
      setData(r.data);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleSignal = (sid) => {
    const cur = data.signal_ids || [];
    const next = cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    patch({ signal_ids: next });
  };

  const toggleResearch = (step) => {
    const cur = data.research_checklist || {};
    patch({ research_checklist: { ...cur, [step]: !cur[step] } });
  };

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post("/opportunity/ai/summary", { prospect_id: prospectId });
      setData((d) => ({ ...d, ai_summary: r.data?.summary, ai_summary_generated_at: new Date().toISOString() }));
      toast.success("AI summary generated.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const [draftBusy, setDraftBusy] = useState(false);
  // Poll for the draft to land while a research run is in progress for this prospect.
  useEffect(() => {
    if (data?.bulk_research_status !== "running") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/opportunity/prospects/${prospectId}`);
        setData(r.data);
        if (r.data?.bulk_research_status !== "running") {
          clearInterval(t);
          if (r.data?.outreach_draft?.subject) {
            toast.success("Outreach draft ready.");
          } else if (r.data?.bulk_research_status === "failed") {
            toast.error("Draft generation failed.");
          }
        }
      } catch {
        // swallow — next tick will retry
      }
    }, 4000);
    return () => clearInterval(t);
  }, [data?.bulk_research_status, prospectId]);

  const draftOutreach = async () => {
    setDraftBusy(true);
    try {
      const r = await api.post("/opportunity/prospects/bulk-research", {
        prospect_ids: [prospectId],
      });
      if (r.data?.queued === 0) {
        toast("Already drafting — please wait.");
      } else {
        toast.success("Claude is drafting your outreach email…");
        // Optimistically flip the local status so the polling effect kicks in.
        setData((d) => ({ ...d, bulk_research_status: "running" }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDraftBusy(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Copy failed.");
    }
  };

  const [sendBusy, setSendBusy] = useState(false);
  const sendDraft = async () => {
    if (!data?.contact_email) {
      toast.error("Add a contact email to the prospect first.");
      return;
    }
    if (!data?.outreach_draft?.subject || !data?.outreach_draft?.body) {
      toast.error("Generate a draft first.");
      return;
    }
    if (
      !window.confirm(
        `Send this draft to ${data.contact_email} via Resend?\n\n` +
          `Subject: ${data.outreach_draft.subject}\n\n` +
          "The prospect's status will move to 'contacted'."
      )
    ) {
      return;
    }
    setSendBusy(true);
    try {
      const r = await api.post(`/opportunity/prospects/${prospectId}/send-draft`, {});
      toast.success(`Sent to ${r.data?.recipient}.`);
      // Refresh detail to pick up the new log entry + status bump.
      await load();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendBusy(false);
    }
  };

  const startNurture = async () => {
    if (
      !window.confirm(
        "Queue a 2-touch AI follow-up sequence (day +4 and +10)?\n\n" +
          "Claude will auto-draft the emails before each one fires.\n" +
          "The sequence stops automatically if the prospect clicks any prior email."
      )
    ) {
      return;
    }
    setNurtureBusy(true);
    try {
      const r = await api.post(`/opportunity/prospects/${prospectId}/start-nurture`, {});
      setSequences((cur) => [r.data, ...cur]);
      toast.success("Nurture sequence queued. Claude is drafting touch #1.");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNurtureBusy(false);
    }
  };

  const stopNurture = async (sid) => {
    if (!window.confirm("Stop this sequence? Any pending follow-ups will never fire.")) return;
    try {
      await api.post(`/opportunity/nurture/${sid}/stop`, {});
      // Reload sequences only — keeps the rest of the dialog stable.
      const s = await api.get(`/opportunity/prospects/${prospectId}/nurture`);
      setSequences(s.data?.sequences || []);
      toast.success("Sequence stopped.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const convert = async () => {
    if (!window.confirm("Convert this prospect to your Clinic Pipeline + create an outreach task?")) return;
    setConvertBusy(true);
    try {
      const r = await api.post(`/opportunity/prospects/${prospectId}/convert`);
      if (r.data?.already_converted) {
        toast("Already converted earlier.");
      } else {
        toast.success("Converted to Clinic Pipeline.");
      }
      load();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConvertBusy(false);
    }
  };

  const addTask = async () => {
    if (!newTask.title.trim()) {
      toast.error("Task title is required.");
      return;
    }
    try {
      await api.post("/opportunity/tasks", {
        prospect_id: prospectId,
        title: newTask.title,
        channel: newTask.channel,
        due_date: newTask.due_date || null,
      });
      setNewTask({ title: "", channel: "email", due_date: "" });
      const t = await api.get(`/opportunity/tasks?prospect_id=${prospectId}`);
      setTasks(t.data?.tasks || []);
      toast.success("Outreach task added.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const taskDone = async (t) => {
    await api.patch(`/opportunity/tasks/${t.id}`, { status: t.status === "done" ? "todo" : "done" });
    const r = await api.get(`/opportunity/tasks?prospect_id=${prospectId}`);
    setTasks(r.data?.tasks || []);
  };

  const removeTask = async (tid) => {
    if (!window.confirm("Delete this outreach task?")) return;
    await api.delete(`/opportunity/tasks/${tid}`);
    const r = await api.get(`/opportunity/tasks?prospect_id=${prospectId}`);
    setTasks(r.data?.tasks || []);
  };

  const removeProspect = async () => {
    if (!window.confirm("Delete this prospect and all its outreach tasks?")) return;
    await api.delete(`/opportunity/prospects/${prospectId}`);
    onChanged();
    onClose();
  };

  return (
    <ModalShell
      title={data?.clinic_name || "Prospect"}
      eyebrow={data ? `${data.intent_tier?.toUpperCase()} intent · ${data.intent_score}/100` : "Loading…"}
      onClose={onClose}
      testId="opp-detail-dialog"
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <button
            type="button"
            onClick={removeProspect}
            data-testid="opp-detail-delete"
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-rose-600 hover:bg-rose-50"
          >
            <Trash2 className="w-3 h-3 inline mr-1" />Delete
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
              data-testid="opp-detail-close"
            >
              Close
            </button>
            <button
              type="button"
              onClick={convert}
              disabled={convertBusy || data?.status === "converted"}
              data-testid="opp-detail-convert"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-50"
            >
              <ArrowRight className="w-3 h-3" />
              {data?.status === "converted" ? "Already converted" : (convertBusy ? "Converting…" : "Convert to Pipeline")}
            </button>
          </div>
        </div>
      }
    >
      {loading || !data ? (
        <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
      ) : (
        <div className="space-y-5">
          {/* Header bar — score + angle + status selector */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Recommended angle</div>
              <div className="text-sm font-bold text-blue-700" data-testid="opp-detail-angle">
                {data.outreach_angle || "Not enough signals yet."}
              </div>
            </div>
            <select
              value={data.status}
              onChange={(e) => patch({ status: e.target.value })}
              data-testid="opp-detail-status"
              disabled={saving}
              className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold"
            >
              {(catalog.statuses || []).map((s) => (
                <option key={s} value={s}>{STATUS_LABEL(s)}</option>
              ))}
            </select>
          </div>

          {/* Public signals */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700 mb-2">
              Public signals · click to toggle
            </div>
            <SignalGrid
              catalog={catalog}
              selected={data.signal_ids || []}
              onToggle={toggleSignal}
              testIdPrefix="opp-detail-signal"
            />
          </section>

          {/* Research checklist */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700 mb-2">
              <ListChecks className="w-3.5 h-3.5 inline mr-1" />
              Research checklist
            </div>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {(catalog.research_steps || []).map((step) => {
                const checked = !!data.research_checklist?.[step];
                return (
                  <label
                    key={step}
                    className="flex items-center gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleResearch(step)}
                      data-testid={`opp-detail-research-${step}`}
                    />
                    <span className={checked ? "text-slate-400 line-through" : "text-slate-700"}>
                      {STATUS_LABEL(step)}
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          {/* Outreach draft (AI-generated subject + body) */}
          <section data-testid="opp-detail-outreach-draft">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
                <Mail className="w-3.5 h-3.5 inline mr-1" />
                Outreach email draft
              </div>
              <button
                type="button"
                onClick={draftOutreach}
                disabled={draftBusy || data.bulk_research_status === "running"}
                data-testid="opp-detail-draft-outreach"
                className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold px-2 py-1 hover:bg-blue-700 disabled:opacity-50"
              >
                <Sparkles className="w-3 h-3" />
                {data.bulk_research_status === "running"
                  ? "Drafting…"
                  : (data.outreach_draft?.subject ? "Regenerate" : "Draft with AI")}
              </button>
            </div>
            {data.bulk_research_status === "running" ? (
              <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-4 text-xs text-amber-900 text-center">
                Claude Sonnet 4.5 is researching this prospect and drafting a personalized email…
              </div>
            ) : data.outreach_draft?.subject ? (
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                    Subject
                  </div>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(data.outreach_draft.subject, "Subject")}
                    data-testid="opp-detail-copy-subject"
                    className="text-[10px] font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                  >
                    <Copy className="w-2.5 h-2.5" /> Copy
                  </button>
                </div>
                <div className="px-3 py-2 text-sm font-semibold text-slate-900" data-testid="opp-detail-draft-subject">
                  {data.outreach_draft.subject}
                </div>
                <div className="flex items-center justify-between px-3 py-2 border-y border-slate-100 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                    Body
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => copyToClipboard(data.outreach_draft.body, "Body")}
                      data-testid="opp-detail-copy-body"
                      className="text-[10px] font-semibold text-blue-700 hover:underline inline-flex items-center gap-1"
                    >
                      <Copy className="w-2.5 h-2.5" /> Copy
                    </button>
                    {data.contact_email && (
                      <a
                        href={`mailto:${data.contact_email}?subject=${encodeURIComponent(data.outreach_draft.subject)}&body=${encodeURIComponent(data.outreach_draft.body)}`}
                        data-testid="opp-detail-mailto"
                        className="text-[10px] font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1"
                      >
                        <Mail className="w-2.5 h-2.5" /> Open in email
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={sendDraft}
                      disabled={sendBusy || !data.contact_email}
                      data-testid="opp-detail-send-draft"
                      title={data.contact_email ? "Send this draft via Resend" : "Add a contact email to enable sending"}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] font-semibold px-2 py-0.5 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Send className="w-2.5 h-2.5" />
                      {sendBusy ? "Sending…" : "Send via Resend"}
                    </button>
                  </div>
                </div>
                <pre
                  className="px-3 py-3 text-xs text-slate-800 whitespace-pre-wrap font-sans leading-relaxed"
                  data-testid="opp-detail-draft-body"
                >
                  {data.outreach_draft.body}
                </pre>
                {data.outreach_draft.generated_at && (
                  <div className="px-3 pb-2 text-[10px] text-slate-400 tabular">
                    Generated {fmtDate(data.outreach_draft.generated_at)} · Claude Sonnet 4.5
                  </div>
                )}
                {Array.isArray(data.outreach_sent_log) && data.outreach_sent_log.length > 0 && (
                  <div
                    className="border-t border-slate-100 bg-emerald-50/40 px-3 py-2"
                    data-testid="opp-detail-sent-log"
                  >
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800 mb-1">
                      Sent via Resend · {data.outreach_sent_log.length}
                    </div>
                    <div className="space-y-0.5">
                      {data.outreach_sent_log
                        .slice()
                        .reverse()
                        .slice(0, 3)
                        .map((s) => (
                          <div
                            key={s.id || s.sent_at}
                            data-testid={`opp-detail-sent-${s.id || s.sent_at}`}
                            className="text-[10px] text-slate-700 tabular flex items-center flex-wrap gap-x-2 gap-y-0.5"
                          >
                            <Send className="w-2.5 h-2.5 text-emerald-600" />
                            <span className="font-semibold">{fmtDate(s.sent_at)}</span>
                            <span className="text-slate-500">→</span>
                            <span className="truncate">{s.recipient}</span>
                            {s.resend_id && (
                              <span className="text-slate-400 font-mono">
                                · {String(s.resend_id).slice(0, 8)}
                              </span>
                            )}
                            {s.opened_at && (
                              <span
                                data-testid={`opp-sent-opened-${s.id || s.sent_at}`}
                                className="ml-1 inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[9px] font-semibold"
                              >
                                opened
                              </span>
                            )}
                            {s.clicked_at && (
                              <span
                                data-testid={`opp-sent-clicked-${s.id || s.sent_at}`}
                                className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 px-1.5 py-0.5 text-[9px] font-semibold"
                              >
                                clicked
                              </span>
                            )}
                            {s.bounced_at && (
                              <span
                                data-testid={`opp-sent-bounced-${s.id || s.sent_at}`}
                                className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 px-1.5 py-0.5 text-[9px] font-semibold"
                              >
                                bounced
                              </span>
                            )}
                            {s.complained_at && (
                              <span
                                data-testid={`opp-sent-complained-${s.id || s.sent_at}`}
                                className="inline-flex items-center gap-1 rounded-full bg-rose-200 text-rose-900 px-1.5 py-0.5 text-[9px] font-semibold"
                              >
                                complaint
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : data.bulk_research_status === "failed" ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-xs text-rose-800">
                Draft failed: {data.bulk_research_error || "unknown error"}. Try again.
              </div>
            ) : (
              <div className="text-xs text-slate-500 py-3 px-3 rounded-lg border border-dashed border-slate-300 bg-white">
                No draft yet. Click "Draft with AI" to have Claude write a signal-grounded outreach email you can copy + send.
              </div>
            )}
          </section>

          {/* Nurture sequences — AI-drafted follow-up cadence */}
          <section data-testid="opp-detail-nurture">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
                <Zap className="w-3.5 h-3.5 inline mr-1" />
                Nurture follow-up sequence
              </div>
              <button
                type="button"
                onClick={startNurture}
                disabled={
                  nurtureBusy ||
                  !(data.outreach_sent_log && data.outreach_sent_log.length > 0) ||
                  sequences.some((s) => s.status === "active")
                }
                data-testid="opp-detail-start-nurture"
                title={
                  !(data.outreach_sent_log && data.outreach_sent_log.length > 0)
                    ? "Send the first outreach email before starting a nurture sequence."
                    : sequences.some((s) => s.status === "active")
                    ? "A sequence is already active for this prospect."
                    : "Queue a 2-touch AI follow-up sequence (+4 days, +10 days)."
                }
                className="inline-flex items-center gap-1 rounded-full bg-amber-600 text-white text-[10px] font-semibold px-2 py-1 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Zap className="w-3 h-3" />
                {nurtureBusy ? "Queuing…" : "Start nurture"}
              </button>
            </div>
            {sequences.length === 0 ? (
              <div className="text-xs text-slate-500 py-3 px-3 rounded-lg border border-dashed border-slate-300 bg-white">
                No nurture sequence yet. After the first outreach is sent, kick a 2-touch
                AI follow-up cadence that auto-stops if the prospect clicks any earlier email.
              </div>
            ) : (
              <div className="space-y-2">
                {sequences.map((seq) => (
                  <div
                    key={seq.id}
                    data-testid={`opp-detail-nurture-seq-${seq.id}`}
                    className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                  >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${
                            seq.status === "active"
                              ? "bg-amber-100 text-amber-800"
                              : seq.status === "completed"
                              ? "bg-emerald-100 text-emerald-800"
                              : seq.status?.startsWith("stopped")
                              ? "bg-slate-200 text-slate-700"
                              : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          {seq.status}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {seq.id.slice(0, 8)}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          {seq.steps?.length || 0} touch{(seq.steps?.length || 0) === 1 ? "" : "es"}
                        </span>
                      </div>
                      {seq.status === "active" && (
                        <button
                          type="button"
                          onClick={() => stopNurture(seq.id)}
                          data-testid={`opp-detail-stop-nurture-${seq.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-700 hover:underline"
                        >
                          <StopCircle className="w-3 h-3" /> Stop
                        </button>
                      )}
                    </div>
                    <div className="divide-y divide-slate-100">
                      {(seq.steps || []).map((step, idx) => {
                        const isPast =
                          step.status === "sent" || step.status === "failed";
                        const dueDate = step.due_at ? new Date(step.due_at) : null;
                        return (
                          <div
                            key={idx}
                            data-testid={`opp-detail-nurture-step-${seq.id}-${idx}`}
                            className="px-3 py-2 flex items-start gap-2 text-[11px]"
                          >
                            <Timer
                              className={`w-3 h-3 mt-0.5 ${
                                step.status === "sent"
                                  ? "text-emerald-600"
                                  : step.status === "failed"
                                  ? "text-rose-600"
                                  : "text-amber-600"
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-slate-700 capitalize">
                                  {step.touch_label || `touch #${idx + 1}`}
                                </span>
                                <span className="text-slate-500">
                                  +{step.offset_days}d
                                </span>
                                {dueDate && (
                                  <span className="text-slate-500 tabular">
                                    {step.status === "sent"
                                      ? `sent ${fmtDate(step.sent_at)}`
                                      : `due ${fmtDate(step.due_at)}`}
                                  </span>
                                )}
                                <span
                                  className={`text-[9px] font-semibold uppercase rounded-full px-1.5 py-0.5 ${
                                    step.status === "sent"
                                      ? "bg-emerald-100 text-emerald-800"
                                      : step.status === "failed"
                                      ? "bg-rose-100 text-rose-800"
                                      : step.draft_status === "ready"
                                      ? "bg-blue-100 text-blue-800"
                                      : step.draft_status === "failed"
                                      ? "bg-rose-100 text-rose-800"
                                      : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {step.status === "sent"
                                    ? "sent"
                                    : step.status === "failed"
                                    ? "send failed"
                                    : step.draft_status === "ready"
                                    ? "draft ready"
                                    : step.draft_status === "failed"
                                    ? "draft failed"
                                    : "drafting…"}
                                </span>
                              </div>
                              {step.subject && (
                                <div
                                  className="mt-0.5 text-slate-700 font-medium truncate"
                                  title={step.subject}
                                >
                                  {step.subject}
                                </div>
                              )}
                              {step.send_error && (
                                <div className="mt-0.5 text-rose-700 text-[10px]">
                                  {step.send_error}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* AI summary */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
                AI outreach summary
              </div>
              <button
                type="button"
                onClick={generateAI}
                disabled={aiBusy}
                data-testid="opp-detail-ai-generate"
                className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold px-2 py-1 hover:bg-slate-800 disabled:opacity-50"
              >
                <Sparkles className="w-3 h-3" /> {aiBusy ? "Thinking…" : (data.ai_summary ? "Regenerate" : "Generate")}
              </button>
            </div>
            {data.ai_summary ? (
              <div className="rounded-lg bg-slate-900 text-slate-100 p-3 text-xs whitespace-pre-wrap leading-relaxed" data-testid="opp-detail-ai-result">
                {data.ai_summary}
              </div>
            ) : (
              <div className="text-xs text-slate-500 py-3 px-3 rounded-lg border border-dashed border-slate-300 bg-white">
                Manually trigger Claude to draft a 5-bullet outreach summary using current signals + notes.
              </div>
            )}
          </section>

          {/* ROI scenarios captured during demos */}
          {(data.roi_scenarios && data.roi_scenarios.length > 0) && (
            <section data-testid="opp-detail-roi-scenarios">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
                  ROI scenarios shown in demos
                </div>
                <span className="text-[10px] text-slate-500">
                  {data.roi_scenarios.length} saved
                </span>
              </div>
              <div className="space-y-2">
                {data.roi_scenarios
                  .slice()
                  .reverse()
                  .map((s) => (
                    <div
                      key={s.id}
                      data-testid={`opp-roi-scenario-${s.id}`}
                      className="rounded-xl border border-slate-200 bg-white p-3"
                    >
                      <div className="flex items-baseline justify-between gap-3 mb-2">
                        <div className="font-semibold text-sm text-slate-900 truncate">
                          {s.label || "Untitled scenario"}
                        </div>
                        <div className="text-[10px] text-slate-500 whitespace-nowrap">
                          {fmtDate(s.saved_at)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded bg-slate-900 text-white px-2 py-1.5">
                          <div className="text-[9px] uppercase tracking-wider font-semibold text-blue-300">
                            Net/mo
                          </div>
                          <div className="font-display font-bold text-base tabular">
                            ${Math.round(s.outputs?.net_monthly || 0).toLocaleString()}
                          </div>
                        </div>
                        <div className="rounded bg-slate-50 border border-slate-200 px-2 py-1.5">
                          <div className="text-[9px] uppercase tracking-wider font-semibold text-slate-500">
                            Net/yr
                          </div>
                          <div className="font-display font-bold text-base tabular text-slate-900">
                            ${Math.round(s.outputs?.net_annualized || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 text-[10px] text-slate-500 tabular">
                        {Math.round(s.inputs?.conversations || 0)} convos · {s.inputs?.conversion_pct || 0}% conv · ${Math.round(s.inputs?.price || 0)} price
                      </div>
                      {s.notes && (
                        <div className="mt-2 text-xs text-slate-700 leading-relaxed border-t border-slate-100 pt-2">
                          {s.notes}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </section>
          )}

          {/* Outreach tasks */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700 mb-2">
              Outreach tasks
            </div>
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 mb-2">
              {tasks.length === 0 && (
                <div className="text-xs text-slate-500 p-3 text-center">No outreach tasks yet.</div>
              )}
              {tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-2 p-2.5 text-xs" data-testid={`opp-task-${t.id}`}>
                  <button
                    type="button"
                    onClick={() => taskDone(t)}
                    data-testid={`opp-task-toggle-${t.id}`}
                    className={`w-4 h-4 rounded-full inline-flex items-center justify-center border ${
                      t.status === "done" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300"
                    }`}
                  >
                    {t.status === "done" && <Check className="w-2.5 h-2.5" />}
                  </button>
                  <div className="flex-1">
                    <div className={`font-semibold ${t.status === "done" ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">
                      {STATUS_LABEL(t.channel)} · {t.due_date ? `due ${fmtDate(t.due_date)}` : "no due date"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTask(t.id)}
                    data-testid={`opp-task-delete-${t.id}`}
                    className="text-rose-500 hover:bg-rose-50 rounded p-1"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTask.title}
                onChange={(e) => setNewTask((n) => ({ ...n, title: e.target.value }))}
                placeholder="New outreach task…"
                data-testid="opp-task-title"
                className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs"
              />
              <select
                value={newTask.channel}
                onChange={(e) => setNewTask((n) => ({ ...n, channel: e.target.value }))}
                data-testid="opp-task-channel"
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
              >
                {(catalog.channels || []).map((c) => (
                  <option key={c} value={c}>{STATUS_LABEL(c)}</option>
                ))}
              </select>
              <input
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask((n) => ({ ...n, due_date: e.target.value }))}
                data-testid="opp-task-due"
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
              />
              <button
                type="button"
                onClick={addTask}
                data-testid="opp-task-add"
                className="rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
              >
                <Plus className="w-3 h-3 inline" /> Add
              </button>
            </div>
          </section>

          {/* Notes + follow-up */}
          <section>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Next follow-up date">
                <input
                  type="date"
                  value={data.next_followup_date ? data.next_followup_date.slice(0, 10) : ""}
                  onChange={(e) => patch({ next_followup_date: e.target.value || null })}
                  data-testid="opp-detail-followup"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Source">
                <input
                  type="text"
                  value={data.source || ""}
                  onChange={(e) => setData({ ...data, source: e.target.value })}
                  onBlur={(e) => patch({ source: e.target.value })}
                  data-testid="opp-detail-source"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </Field>
            </div>
            <Field label="Notes">
              <textarea
                rows={3}
                value={data.notes || ""}
                onChange={(e) => setData({ ...data, notes: e.target.value })}
                onBlur={(e) => patch({ notes: e.target.value })}
                data-testid="opp-detail-notes"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </Field>
          </section>
        </div>
      )}
    </ModalShell>
  );
}

// ===========================================================================
// Shared modal shell
